import type { TestimonialDto } from '@portfolio/shared';
import { cn } from '@/lib/utils';
import { quoteText } from './i18n';

/** Quotes longer than this are set smaller, so they don't stretch the card. */
const LONG_QUOTE = 220;
const ARABIC = /[؀-ۿ]/;

/**
 * Large white testimonial card on a dark band: the quote set big, with the client's 1:1 picture
 * and name card beside it. The name card always closes the card: bottom of the side column, and
 * below the quote on phones. Shared by the homepage slider and the project page.
 * Kept compact so the slider's progress bar stays on screen below it.
 */
export function TestimonialFeature({ t, className }: { t: TestimonialDto; className?: string }) {
  const role = [t.clientRole, t.company].filter(Boolean).join(' · ');
  const quote = t.quote.trim();
  const long = quote.length > LONG_QUOTE;
  // Arabic quotes use the English sizes, with more line height to clear the dots and marks
  const arabic = ARABIC.test(quote);
  return (
    <figure
      className={cn(
        // Never taller than the screen below the navbar, leaving room for the progress bar: a long
        // quote scrolls inside the card instead of pushing the bar out of view
        'grid max-h-[calc(100svh-180px)] grid-rows-[minmax(0,1fr)_auto] gap-6 rounded-[28px] bg-card p-5 shadow-[0_0_110px_10px_rgb(255_255_255/0.09)] sm:p-7 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)] md:grid-rows-[minmax(0,1fr)] md:gap-x-[52px] md:rounded-[36px] md:ps-[26px] md:pe-[64px] md:py-[26px]',
        className,
      )}
    >
      <figcaption className="order-last flex flex-col gap-[14px] md:order-none">
        <div className="hidden aspect-square overflow-hidden rounded-[26px] bg-primary shadow-[0_18px_40px_-12px_rgb(0_0_0/0.25)] md:block">
          <Picture t={t} />
        </div>
        <div className="flex items-center gap-4 rounded-[24px] bg-card-raised px-6 pt-5 pb-6 shadow-[0_18px_40px_-12px_rgb(0_0_0/0.18)] md:mt-auto md:rounded-[26px] md:px-[30px]">
          {/* Phones: the picture sits next to the name instead of in its own square */}
          <div className="size-16 shrink-0 overflow-hidden rounded-[16px] bg-primary md:hidden">
            <Picture t={t} small />
          </div>
          <div className="min-w-0">
            <p className="text-title-sm font-medium text-strong">{t.clientName}</p>
            {role && <p className="mt-1 text-ui text-silver">{role}</p>}
          </div>
        </div>
      </figcaption>
      <blockquote
        dir="auto"
        lang={arabic ? 'ar' : undefined}
        className={cn(
          // keep the line breaks the admin typed
          'min-h-0 overflow-y-auto font-bold whitespace-pre-line text-strong [scrollbar-width:thin] md:pt-[5px]',
          // (line height after the size: tailwind-merge drops a leading-* set before a text-* size)
          long ? 'text-lead md:text-title-sm' : 'text-title',
          arabic ? 'leading-[1.5]' : 'leading-[1.25]',
        )}
      >
        {quoteText(quote)}
      </blockquote>
    </figure>
  );
}

/** The client's photo, or their initial on the brand colour when there is none. */
function Picture({ t, small }: { t: TestimonialDto; small?: boolean }) {
  return t.avatar ? (
    <img
      src={small ? (t.avatar.thumbUrl ?? t.avatar.url) : t.avatar.url}
      alt={small ? '' : (t.avatar.alt ?? t.clientName)}
      width={t.avatar.width ?? undefined}
      height={t.avatar.height ?? undefined}
      loading="lazy"
      decoding="async"
      className="size-full object-cover"
    />
  ) : (
    <span
      aria-hidden
      className={cn(
        'flex size-full items-center justify-center font-black tracking-[-0.015em] text-black/85',
        small ? 'text-[28px]' : 'text-[96px]',
      )}
    >
      {t.clientName.charAt(0)}
    </span>
  );
}
